import { FullComposeViewState } from 'owa-calendar-compose-types';
import hasCustomTimeZones from '../utils/hasCustomTimeZones';
import {
    FullComposeFormViewType,
    FULL_COMPOSE_VIEW_TYPE_POPOUT,
} from 'owa-calendar-helpers-types/lib/ComposeTypes';
import SidePaneType from 'owa-calendar-sidepane/lib/schema/SidePaneType';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import createAccCheckerViewState from 'owa-editor-acc-checker/lib/utils/createAccCheckerViewState';
import createEmojiPickerViewState from 'owa-editor-emoji-picker-plugin/lib/utils/createEmojiPickerViewState';
import createRibbonViewState from 'owa-editor-ribbonplugin/lib/utils/createRibbonViewState';
import createEditorViewState from 'owa-editor/lib/utils/createEditorViewState';
import createProofingViewState from 'owa-editor-proofing-plugin/lib/utils/createProofingViewState';
import { createAddinViewState } from 'owa-editor-addin-plugin-types/lib/utils/createAddinViewState';
import createInfoBarHostViewState from 'owa-info-bar/lib/utils/createInfoBarHostViewState';
import createAtMentionsViewState from 'owa-editor-mentions-picker-plugin/lib/utils/createAtMentionsViewState';
import createDropViewState from 'owa-dnd/lib/utils/createDropViewState';
import { getIsDarkTheme } from 'owa-fabric-theme';
import createInlineImageViewState from 'owa-inline-image/lib/utils/createInlineImageViewState';
import getExpressionId from 'owa-expressions-store/lib/utils/getExpressionId';
import { createPasteOptionViewState } from 'owa-editor-paste-option-plugin';
import createInlineImageViewStateV2 from 'owa-editor-inlineimage-plugin/lib/utils/createInlineImageViewState';
import { getDefaultShouldShortenEventDuration } from 'owa-calendar-cloud-settings';
import createDockingViewState from 'owa-editor-docking-plugin/lib/utils/createDockingViewState';
import createRotateImageViewState from 'owa-editor-rotate-image-plugin/lib/utils/createRotateImageViewState';
import { createAugloopSession } from 'augloop-service/lib/utils/createAugloopSession';
import { hasVisibleContent } from 'owa-calendar-forms-utils/lib/utils/hasVisibleContent';

const MIN_BODY_HEIGHT = 72;
const COMPACT_WELL_THRESHOLD = 10;
const COMPOSE_SCHEDULING_GRID_SCENARIO_ID = 'ComposeSchedulingGridScenario';

export default function createComposeViewState(
    event: CalendarEvent,
    isCharmExplicitlySet: boolean,
    viewType: FullComposeFormViewType,
    isAcceptPNT: boolean,
    extractionSourceId: string,
    internetMessageId?: string,
    collabSpaceViewStateId?: string
): FullComposeViewState {
    let attendeeCount: number = event.RequiredAttendees?.length + event.OptionalAttendees?.length;

    let viewState: FullComposeViewState = {
        ...createInfoBarHostViewState(),
        docking: createDockingViewState(MIN_BODY_HEIGHT, true /*allowStickyDocking*/),
        dropViewState: createDropViewState(),
        isCharmExplicitlySet: isCharmExplicitlySet,
        freeBusyData: undefined,
        mentionsPicker: createAtMentionsViewState(null),
        attachmentWell: undefined,
        bodyViewState: createEditorViewState(event.Body ? event.Body.Value : null),
        isOptionalAttendeeWellEnabled: event.OptionalAttendees?.length > 0,
        shouldFocusOptionalAttendeeWellOnMount: false,
        viewType: viewType,
        ribbon: createRibbonViewState(),
        isRibbonShown: false,
        showExpandedEditor: event.Body && hasVisibleContent(event.Body.Value),
        emojiPicker: createEmojiPickerViewState(),
        accChecker: createAccCheckerViewState(false /*useFlexPane*/),
        inlineImage: createInlineImageViewState(),
        inlineImageV2: createInlineImageViewStateV2(),
        customRecurrenceViewState: {
            recurrence: null,
            startDate: null,
        },
        subjectViewState: {
            selectionStart: 0,
            selectionEnd: 0,
        },
        currentSidePaneType: SidePaneType.None,
        showSchedulingAssistant: false,
        showCompactRecipientWell: attendeeCount > COMPACT_WELL_THRESHOLD,
        showDateSelectorTimeZones: hasCustomTimeZones(event.Start.tz, event.End.tz),
        isUndoNotificationShown: false,
        schedulingGridScenarioId: COMPOSE_SCHEDULING_GRID_SCENARIO_ID,
        currentSaveRequestTimeStamp: null,
        isAcceptProposeNewTime: isAcceptPNT,
        extractionSourceId: extractionSourceId,
        internetMessageId: internetMessageId,
        awaitingServiceResponse: false,
        unresolvedLocation: null,
        linkFiles: [],
        addin: createAddinViewState(event.AppendOnSend),
        uiEnabled: true,
        isCompactRecipientWellExpanded: false,
        isDarkMode: getIsDarkTheme(),
        typeOfItemBeingDragged: null,
        showDropHint: false,
        proofing: createProofingViewState(),
        isOnlineMeetingExplicitlySet: false,
        expressionId: getExpressionId(),
        pasteOption: createPasteOptionViewState(),
        rotateImage: createRotateImageViewState(),
        shouldAdjustStartTimeEndTime:
            getDefaultShouldShortenEventDuration() || viewType == FULL_COMPOSE_VIEW_TYPE_POPOUT,
        augloopSession: createAugloopSession(),
        isAllDayEventExplicitlySet: false,
        collabSpaceViewStateId: collabSpaceViewStateId,
    };

    // Show 1 item first then it will recalculate
    viewState.ribbon.visibleItemCount = 1;

    return viewState;
}
